import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Zap, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const nudge = (p) => {
  const t = p.today;
  if (t.bonus_enabled) {
    if (t.bonus_earned) return `🎉 Today's ${t.bonus_amount} ${p.currency} bonus is yours!`;
    if (t.failed > 0) return `Today's bonus needs a clean day — keep crushing the rest! 💪`;
    if (t.remaining === 0 && t.delivered > 0 && t.delivered < t.min_assigned)
      return `Deliver ${t.min_assigned - t.delivered} more today to qualify for the ${t.bonus_amount} ${p.currency} bonus!`;
    if (t.remaining > 0)
      return `🔥 ${t.remaining} more ${t.remaining === 1 ? 'delivery' : 'deliveries'} to earn today's ${t.bonus_amount} ${p.currency} bonus!`;
  }
  if (p.month.next_slab)
    return `🚀 ${p.month.next_slab.deliveries_needed} more deliveries unlocks ${p.month.next_slab.rate} ${p.currency}/delivery!`;
  return `Great pace — every delivery adds to your payout! 💪`;
};

export const DriverIncentiveWidget = ({ refreshSignal }) => {
  const [progress, setProgress] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_BASE_URL}/delivery-incentives/my-progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setProgress(r.data);
      } catch (e) {
        if (active) setProgress(null);
      }
    })();
    return () => { active = false; };
  }, [refreshSignal]);

  if (!progress || !progress.enabled || !progress.eligible) return null;

  const { month, today, same_day_month, projected_total, currency } = progress;
  const pct = today.assigned > 0 ? Math.round((today.delivered / today.assigned) * 100) : 0;

  return (
    <div className="mx-4 mt-3 rounded-2xl bg-wt-surface border border-wt-border overflow-hidden" data-testid="driver-incentive-widget">
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between" onClick={() => setExpanded(!expanded)} data-testid="incentive-widget-header">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-wt-gold" />
            <span className="font-bold text-wt-gold">My Incentives</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-green-400" data-testid="projected-total">
              ~{projected_total} {currency} this month
            </span>
            {expanded ? <ChevronUp className="w-4 h-4 text-wt-text-muted" /> : <ChevronDown className="w-4 h-4 text-wt-text-muted" />}
          </div>
        </div>

        {/* Today progress */}
        {today.bonus_enabled && (
          <div data-testid="today-bonus-section">
            <div className="flex justify-between text-xs text-wt-text-muted mb-1">
              <span>Today: {today.delivered}/{today.assigned} delivered</span>
              {today.bonus_earned && <span className="text-green-400 font-semibold">Bonus earned ✓</span>}
            </div>
            <div className="h-2.5 rounded-full bg-wt-bg overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${today.bonus_earned ? 'bg-green-500' : 'bg-wt-gold'}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          </div>
        )}

        <p className="text-sm font-medium text-wt-text" data-testid="incentive-nudge">{nudge(progress)}</p>

        {expanded && (
          <div className="pt-2 border-t border-wt-border space-y-2 text-sm" data-testid="incentive-details">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-wt-text-muted"><TrendingUp className="w-4 h-4" /> Deliveries this month</span>
              <span className="font-semibold text-wt-text">{month.delivered}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-wt-text-muted">Current rate</span>
              <span className="font-semibold text-wt-text">{month.rate} {currency}/delivery = {month.slab_amount} {currency}</span>
            </div>
            {month.next_slab && (
              <div className="flex items-center justify-between">
                <span className="text-wt-text-muted">Next slab at {month.next_slab.at}</span>
                <span className="font-semibold text-wt-gold">{month.next_slab.rate} {currency}/delivery</span>
              </div>
            )}
            {today.bonus_enabled && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-wt-text-muted"><Zap className="w-4 h-4 text-yellow-400" /> Same-day bonuses</span>
                <span className="font-semibold text-wt-text">{same_day_month.days} days = {same_day_month.total} {currency}</span>
              </div>
            )}
            <p className="text-[11px] text-wt-text-muted pt-1">Credited to your ledger with monthly payroll.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverIncentiveWidget;
